import { useCallback, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { dealApi, dealQueryKeys } from '@crm/entities/deal';
import { toastManager } from '@crm/entities/toast';
import { getApiErrorMessage } from '@crm/shared/api';
import { DateHelpers } from '@crm/shared/lib';
import { tasksActions, tasksInformationActions } from '@entities/task';
import { appendSearchParams, deleteSearchParams } from '@shared/helpers';
import { useAppDispatch, useAppSelector } from 'store';
import { getUpdatedFieldErrors } from 'widgets/Modals/lib/getUpdatedFieldErrors';
import {
    changeEditModalDescription,
    changeEditModalIsCompleted,
    changeEditModalTitle,
    resetEditModalData,
    setEditModalData,
} from 'widgets/Modals/model/slice/editModalSlice';

import { mapDealTasksDataToTasksInformation } from './mapDealTasksDataToTasksInformation';
import { mapDealTaskToEditModalTask, mapDealTaskToLegacyTask } from './mapDealTaskToLegacyTask';

import type { TDeal, TDealTasksData } from '@crm/entities/deal';
import type { IModalAttachment } from '@entities/attachment';
import type { FieldErrors } from '@entities/modal';
import type { IAttachmentResponse, TaskData } from '@entities/task';
import type { TAPIError } from 'widgets/Modals/model/types/apiError.type';

const getInitialFieldErrors = (): FieldErrors => ({
    title: { isError: false, errorMessage: null },
    description: { isError: false, errorMessage: null },
    objectOrEstimate: { isError: false, errorMessage: null },
    executor: { isError: false, errorMessage: null },
    tags: { isError: false, errorMessage: null },
    date: { isError: false, errorMessage: null },
});

const formatTaskDate = (date: string | null) => {
    if (!date || date.length === 1) {
        return '';
    }

    const parsedDate = DateHelpers.parseDisplayDate(date) ?? DateHelpers.parseApiDate(date);

    return parsedDate ? DateHelpers.formatApiDate(parsedDate) : '';
};

const appendNullableNumber = (formData: FormData, name: string, value: number | '' | null) => {
    if (value !== '' && value !== null) {
        formData.append(name, String(value));
    }
};

const isNewAttachment = (
    attachment: IModalAttachment | IAttachmentResponse
): attachment is IModalAttachment => 'file' in attachment;

const getTaskEntityUid = (task?: TaskData) => task?.context?.entity_uid ?? task?.uid ?? '';

export const useEditDealTaskModal = ({
    dealId,
    tasksData,
}: {
    dealId: TDeal['id'];
    tasksData?: TDealTasksData;
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const editModalData = useAppSelector((state) => state.editModalReducer);
    const [isOpen, setIsOpen] = useState(false);
    const [taskData, setTaskData] = useState<TaskData | undefined>();
    const [fileIdToPreview, setFileIdToPreview] = useState<string | null>(null);
    const [fileIdToDeleteFromList, setFileIdToDeleteFromList] = useState<string | null>(null);
    const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
    const [deleteConfirmPopup, setDeleteConfirmPopup] = useState(false);
    const [attachments, setAttachments] = useState<(IModalAttachment | IAttachmentResponse)[]>([]);
    const [previewEnable, setPreviewEnable] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>(getInitialFieldErrors);
    const [triggerAnimationError, setTriggerAnimationError] = useState(false);
    const [modalData, setModalData] = useState({
        taskTitle: '',
        taskDescription: '',
        date: '',
    });
    const [isCheckedRadio, setIsCheckedRadio] = useState(false);
    const isObserver = taskData?.context?.highest_role === 'observer';

    const resetModal = useCallback(() => {
        setFieldErrors(getInitialFieldErrors());
        setAttachments([]);
        setPreviewEnable(false);
        setFileIdToPreview(null);
        setFileIdToDeleteFromList(null);
        setDeleteConfirmModal(false);
        setDeleteConfirmPopup(false);
        setTriggerAnimationError(false);
        setModalData({
            taskTitle: '',
            taskDescription: '',
            date: '',
        });
        setIsCheckedRadio(false);
        setTaskData(undefined);
        dispatch(resetEditModalData());
        deleteSearchParams('task_id');
    }, [dispatch]);

    const buildUpdateDealTaskFormData = () => {
        const formData = new FormData();
        const includedAttachments = attachments.filter(
            (attachment) => !isNewAttachment(attachment)
        );

        formData.append('title', editModalData.title.trim());
        formData.append('description', editModalData.description);
        formData.append('color', editModalData.color);
        formData.append('date', formatTaskDate(editModalData.dateEnd));
        formData.append('is_completed', editModalData.isCompleted ? '1' : '0');
        formData.append('status', editModalData.status);

        editModalData.tags.forEach((tag) => {
            formData.append('tags[]', tag);
        });

        editModalData.observerIds.forEach((observerId) => {
            formData.append('observer_ids[]', String(observerId));
        });

        attachments.forEach((attachment) => {
            if (isNewAttachment(attachment)) {
                formData.append('new_attachments[]', attachment.file);
            }
        });

        if (includedAttachments.length > 0) {
            includedAttachments.forEach((attachment) => {
                formData.append('included_attachments[]', String(attachment.id));
            });
        } else {
            formData.append('included_attachments[]', '');
        }

        appendNullableNumber(formData, 'executor_id', editModalData.executorId);
        appendNullableNumber(formData, 'project_id', editModalData.projectId);
        appendNullableNumber(formData, 'estimate_id', editModalData.estimateId);

        return formData;
    };

    const updateDealTaskMutation = useMutation({
        mutationKey: [...dealQueryKeys.detail(dealId), 'tasks', 'update'],
        mutationFn: () =>
            dealApi.updateDealTask({
                dealId,
                taskUid: getTaskEntityUid(taskData),
                payload: buildUpdateDealTaskFormData(),
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: dealQueryKeys.detail(dealId) });
            toastManager.onSuccess({
                title: t('crm.dealDetailsContent.tasksBlock.notifications.updateSuccess'),
            });
            setIsOpen(false);
            resetModal();
        },
        onError: (error) => {
            toastManager.onError({
                title: getApiErrorMessage(
                    error,
                    t('crm.dealDetailsContent.tasksBlock.errors.update')
                ),
            });
        },
    });

    const deleteDealTaskMutation = useMutation({
        mutationKey: [...dealQueryKeys.detail(dealId), 'tasks', 'delete'],
        mutationFn: () =>
            dealApi.deleteDealTask({
                dealId,
                taskUid: getTaskEntityUid(taskData),
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: dealQueryKeys.detail(dealId) });
            toastManager.onSuccess({
                title: t('crm.dealDetailsContent.tasksBlock.notifications.deleteSuccess'),
            });
            setIsOpen(false);
            resetModal();
        },
        onError: (error) => {
            toastManager.onError({
                title: getApiErrorMessage(
                    error,
                    t('crm.dealDetailsContent.tasksBlock.errors.delete')
                ),
            });
        },
    });

    const onOpenModal = (task: TDealTasksData['tasks'][number]) => {
        const legacyTask = mapDealTaskToLegacyTask(task);
        const tagTitles =
            tasksData?.tags
                ?.filter((tag) => task.context.tags?.includes(tag.id))
                .map((tag) => tag.title) ?? [];

        dispatch(tasksActions.addTasks(tasksData?.tasks?.map(mapDealTaskToLegacyTask) ?? []));
        dispatch(
            tasksInformationActions.setTasksInformation(
                mapDealTasksDataToTasksInformation(tasksData)
            )
        );
        dispatch(setEditModalData(mapDealTaskToEditModalTask({ task, tagTitles })));
        deleteSearchParams('task_id');
        appendSearchParams('task_id', task.uid);
        setTaskData(legacyTask);
        setAttachments(legacyTask.attachments ?? []);
        setModalData({
            taskTitle: task.title,
            taskDescription: task.context.description ?? '',
            date: task.dateStart || task.dateEnd || '',
        });
        setIsCheckedRadio(task.completed);
        setIsOpen(true);
    };

    const onHideModal = () => {
        setIsOpen(false);
        resetModal();
    };

    const deleteAttachment = (fileId: string | undefined) => {
        if (!fileId) return;
        setAttachments((prev) => prev.filter((attachment) => String(attachment.id) !== fileId));
        setFileIdToDeleteFromList(null);
        setDeleteConfirmModal(false);
    };

    const onHidePrevModal = useCallback(() => {
        setPreviewEnable(false);
        setFileIdToPreview(null);
    }, []);

    const onEnablePreviewModal = useCallback((enablePreview: boolean, fileId: string) => {
        setPreviewEnable(enablePreview);
        setFileIdToPreview(fileId);
    }, []);

    const handleRadioBtn = () => {
        if (modalData.taskTitle.length === 0 || isObserver) {
            return;
        }

        const nextValue = !isCheckedRadio;
        setIsCheckedRadio(nextValue);
        dispatch(changeEditModalIsCompleted(nextValue));
    };

    const onChangeEditModalTitle = (title: string) => {
        if (isCheckedRadio) return;
        if (fieldErrors.title.isError) {
            setFieldErrors((prev) => ({
                ...prev,
                title: { isError: false, errorMessage: null },
            }));
        }

        setModalData((prev) => ({ ...prev, taskTitle: title }));
        dispatch(changeEditModalTitle(title));
    };

    const onChangeModalDescription = (description: string) => {
        if (isCheckedRadio) return;
        if (fieldErrors.description.isError) {
            setFieldErrors((prev) => ({
                ...prev,
                description: { isError: false, errorMessage: null },
            }));
        }

        setModalData((prev) => ({ ...prev, taskDescription: description }));
        dispatch(changeEditModalDescription(description));
    };

    const onSubmit = async () => {
        if (modalData.taskTitle.trim().length === 0) {
            setFieldErrors((prev) => ({
                ...prev,
                title: {
                    isError: true,
                    errorMessage: t('crm.dealDetailsContent.tasksBlock.validation.titleRequired'),
                },
            }));
            setTriggerAnimationError(true);
            return;
        }

        if (
            fieldErrors.date.isError ||
            fieldErrors.tags.isError ||
            fieldErrors.title.isError ||
            fieldErrors.description.isError ||
            fieldErrors.executor.isError ||
            fieldErrors.objectOrEstimate.isError
        ) {
            return;
        }

        try {
            setFieldErrors(getInitialFieldErrors());
            await updateDealTaskMutation.mutateAsync();
        } catch (error) {
            if (typeof error === 'object' && error !== null && 'errors' in error) {
                const apiErrors = error.errors as TAPIError[];
                const updatedFieldErrors = getUpdatedFieldErrors(fieldErrors, apiErrors);
                setFieldErrors(updatedFieldErrors);
            }
        }
    };

    const deleteTaskHandler = () => {
        deleteDealTaskMutation.mutate();
    };

    const switchDeleteConfirmPopup = () => {
        setDeleteConfirmPopup((prev) => !prev);
    };

    return {
        attachments,
        deleteAttachment,
        deleteConfirmModal,
        deleteConfirmPopup,
        deleteTaskHandler,
        fieldErrors,
        fileIdToDeleteFromList,
        fileIdToPreview,
        handleRadioBtn,
        isCheckedRadio,
        isModalLoading: updateDealTaskMutation.isPending || deleteDealTaskMutation.isPending,
        isObserver,
        modalData,
        modalMode: isOpen ? ('editTask' as const) : null,
        onChangeEditModalTitle,
        onChangeModalDescription,
        onEnablePreviewModal,
        onHideModal,
        onHidePrevModal,
        onOpenModal,
        onSubmit,
        pickedTaskUid: taskData?.uid ?? null,
        previewEnable,
        setAttachments,
        setDeleteConfirmModal,
        setFieldErrors,
        setFileIdToDeleteFromList,
        setTriggerAnimationError,
        switchDeleteConfirmPopup,
        taskData,
        triggerAnimationError,
    };
};
